/**
 * What a session is holding, and what has happened to it since (RFC 0297 §4c).
 *
 * TWO NOUNS, ON PURPOSE. A `Holding` is what the session's OWN LOG says: which
 * document, when it first came into hand, when this session last touched, and
 * what it saw. A `HeldDocument` is that plus what the PROJECT says has
 * happened since, which is a different document and a different clock. Keeping
 * them apart is what lets the fold be tested without a project and lets this
 * answer without opening a single document.
 *
 * ZERO HYDRATION. Staleness reads `updatedAt` off the catalog entry, not off the
 * document, and the count reads the comment map. A bar over twenty holdings
 * would otherwise open twenty Automerge documents on every render, which is the
 * cost the catalog exists to avoid.
 */

import type { Holding } from '../../log'
import { projectComments } from '../comments/reads'
import { type CommentMap, commentListView } from '../comments/store'
import type { CommentEntry } from '../comments/types'
import { projectDocuments } from '../documents/reads'
/** A holding, decorated with what the project says has happened to it. */
export interface HeldDocument extends Holding {
  /** The catalog's title. Falls back to the path, then the id, so a row always
   *  says something rather than rendering blank. */
  title: string
  path: string
  /**
   * The document has been written since this session last touched.
   *
   * A BOOLEAN AND A TIME, never a count. Nothing counts writes, and walking
   * Automerge history per holding per render is not affordable, so claiming a
   * number here would mean inventing one (§4c).
   */
  changed: boolean
  /** When it was last written, present whether or not it counts as changed. */
  updatedAt: number
  /** Comments on this document since the last touch, by anyone else, unresolved. */
  newComments: number
  /** The catalog has no entry for it: deleted, or not yet synced here. */
  missing: boolean
}

/**
 * Unread, from this session's point of view (§4c).
 *
 * Three rules, each excluding something a naive count would include.
 *
 * SINCE `lastReadAt`, not since the holding began and not since the last touch of
 * any kind. Reading the document clears the count, and nothing else does. That
 * used to measure from `lastAt`, which every touch moves: opening the document
 * beside the session, or the agent writing a line in it, cleared the badge for a
 * comment nothing had shown anybody. A document never read has `lastReadAt` of
 * 0, so every comment on it counts, which is the honest answer.
 *
 * NOT ITS OWN, because a session counting its own replies would report unread
 * mail it had just posted. A person's comment carries no `authorSessionId` and
 * so always counts, which is right.
 *
 * NOT RESOLVED, because the number exists to say there is something here for
 * you, and a resolved thread is the case where there is not.
 *
 * A comment on a PART of the document counts toward the document: a canvas
 * layer comment targets `{ documentId, nodeId }`, and `projectComments`
 * constrains only the keys the query names. Where inside it is the row's
 * business, not the count's.
 */
function countNewComments(
  all: CommentEntry[],
  since: number,
  sessionId: string | undefined,
): number {
  return all.filter(
    (c) =>
      c.createdAt > since &&
      (!sessionId || c.authorSessionId !== sessionId) &&
      !c.resolved,
  ).length
}

/**
 * Decorate a session's holdings with the project's answer.
 *
 * `sessionId` is what makes "not its own" answerable. Absent, every comment
 * counts, which is the honest reading when nobody has said whose session this
 * is.
 */
export function decorateHoldings(
  holdings: Holding[],
  projectDoc: unknown,
  sessionId?: string,
  /** RFC 0345 3g: a protected project keeps no index; its caller reads the
   *  held documents' own comments (`countedComments`) and passes them here. */
  listed: CommentMap = commentListView(projectDoc),
): HeldDocument[] {
  if (holdings.length === 0) return []
  const catalog = new Map(projectDocuments(projectDoc).map((d) => [d.id, d]))
  // Counted off the project's index (RFC 0339 3d): who wrote each, when, and
  // whether it is resolved is all a count needs, and no comments document is
  // opened for it.
  return holdings.map((h) => {
    const ref = catalog.get(h.documentId)
    const updatedAt = ref?.updatedAt ?? 0
    const comments = projectComments(listed, {
      documentId: h.documentId,
    })
    return {
      ...h,
      title: ref?.title || ref?.path || h.documentId,
      path: ref?.path ?? '',
      // Strictly ahead. Equal means this session saw exactly these bytes, which
      // is the common case the moment after a read and must not read as stale.
      changed: updatedAt > h.sawUpdatedAt,
      updatedAt,
      newComments: countNewComments(comments, h.lastReadAt, sessionId),
      missing: ref === undefined,
    }
  })
}
